import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "d-flex flex-column" }

import Signature from '@grantstreet/psc-vue/components/Signature.vue'
import UploadApiClass from './upload-api.ts'
import type { SentryMessageFunction } from '@grantstreet/sentry'
import { ref } from 'vue'

type SignatureUploadWidgetProps = {
  id?: string
  name?: string
  sourceTag: string
  retainUntil: string
  disabled?: boolean
  /**
   * An exception logger function (e.g., sentryException) to be used by the
   * Upload Service API for logging API errors
   */
  exceptionLogger: SentryMessageFunction
}

const startDelay = 500
// When a user uploads eight uploads, they are debounced longer
const slowUploadCount = 8
// Five seconds
const slowDelay = 5000
// Stop uploading after 40 uploads. The user will be prompted to refresh.
const stopUploadCount = 40

/**
 * Debounce- don't upload after every stroke.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'SignatureUploadWidget',
  props: {
    id: {},
    name: {},
    sourceTag: {},
    retainUntil: {},
    disabled: { type: Boolean },
    exceptionLogger: { type: Function }
  },
  emits: ["update:modelValue", "update:isProcessing", "update:isBlocked"],
  setup(__props: any, { emit }) {

const props = __props;

/**
 * This is a wrapper around the signature component that uploads signatures
 * to the PSP Uploads Service.
 */




const errorMessage = ref('')

const uploadApi = new UploadApiClass({ exceptionLogger: props.exceptionLogger })

const clearedTime = ref(new Date())

const uploadCount = ref(0)

// Half a second
let timeoutId: ReturnType<typeof setTimeout>
async function debounceUpload (data) {
  emit('update:isProcessing', true)

  // Most users have one stroke signatures.
  // Assume the first stroke doesn't need a debounce.
  if (uploadCount.value === 0) {
    uploadFile(data)
    uploadCount.value++
  }
  else {
    // Debounce subsequent strokes.
    // This'll take some fine tuning to get right. Start at half a second.
    let timeout = startDelay

    // Slow down (increase debounce) as user has changed their signature 8 times.
    if (uploadCount.value >= slowUploadCount) {
      timeout = slowDelay
    }

    // Possibly a bad actor. Stop uploading.
    if (uploadCount.value > stopUploadCount) {
      emit('update:isBlocked', true)
      throw new Error('There is an issue with signatures.')
    }

    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      uploadFile(data, timeout)
      uploadCount.value++
    }, timeout)
  }
}

/**
 * Upload the signature to the server via the uploads service
 * Use with debounceUpload
 */
async function uploadFile (data, timeout = 0) {
  errorMessage.value = ''
  if (!props.sourceTag) {
    throw new Error('sourceTag required')
  }
  if (!props.retainUntil) {
    throw new Error('retainUntil required')
  }

  // If a user clears the signature before the upload finishes, keep blank.
  const startTime = new Date()

  try {
    // Convert base64 image to a file.
    const base64Data = data.split(',')[1]
    // Convert base64 to a buffer
    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    // Create a Blob from the buffer
    const blob = new Blob([byteArray], { type: 'image/png' })
    // Create a File object from the Blob
    const file = new File([blob], 'image.png', { type: 'image/png' })

    // 2MB limit
    if (file.size > 2e+6) {
      throw new Error('This signature is too large.')
    }

    // Retrieve an upload ID and URL
    const {
      data: {
        uploadUri,
        uploadId,
      },
    } = await uploadApi.initiateUpload(props.sourceTag, props.retainUntil)

    // Upload the file to the URL
    await uploadApi.makeUpload(uploadUri, file)

    emit('update:isProcessing', false)

    // Set only if the user hasn't cleared before the upload finishes
    // Account for a debounce / delay.
    clearedTime.value = new Date(clearedTime.value.getTime() + timeout)
    if (startTime > clearedTime.value) {
      // Set the FormKit input value to the signature's upload ID
      emit('update:modelValue', uploadId)
    }
  }
  catch (error) {
    errorMessage.value = 'There was an issue creating your signature, please try again.'
    props.exceptionLogger(error as Error)
    throw new Error('There was an issue with signatures.')
  }
}

function updateCleared () {
  errorMessage.value = ''
  clearedTime.value = new Date()

  // Reset to right before the user is slowed down.
  if (uploadCount.value >= slowUploadCount && uploadCount.value <= stopUploadCount) {
    uploadCount.value = slowUploadCount - 1
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Signature, {
      "process-file": debounceUpload,
      "source-tag": props.sourceTag,
      "error-message": errorMessage.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:modelValue', $event))),
      onClear: updateCleared
    }, null, 8, ["source-tag", "error-message"])
  ]))
}
}

})